import type { Profile } from '../../types';

/**
 * Lista canônica de departamentos do VigorHub.
 * Usada em CreateUserModal, UserManagement e filtros.
 *
 * O Comercial é um departamento só desde 29/10/2026 (#6520): Comercial Interno e
 * Comercial Externo viraram "Comercial", e o que diferencia as pessoas é a função
 * (Closer, BDR, Hunter — ver FUNCOES_COMERCIAIS). Parcerias continua separado.
 */
export const DEPARTAMENTO_COMERCIAL = 'Comercial';

/** Nomes antigos do Comercial. Ficam só para ler cadastro e histórico de antes da troca. */
export const DEPARTAMENTOS_COMERCIAIS_LEGADOS = ['Comercial Interno', 'Comercial Externo', 'Vendas Internas'] as const;

export const DEPARTAMENTOS = [
  DEPARTAMENTO_COMERCIAL,
  'Parcerias',
  'Financeiro',
  'Faturamento',
  'Pós Venda',
  'Cadastro',
  'Jurídico',
  'Indicador',
  'Marketing',
  'Tecnologia',
  'Suporte e Atendimento',
  'Motoboy',
] as const;

export type Departamento = typeof DEPARTAMENTOS[number];

/** Cor de badge por departamento (Tailwind classes) */
export const DEPT_COLOR: Record<string, string> = {
  'Comercial':             'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'Parcerias':             'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  'Financeiro':            'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'Faturamento':           'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  'Pós Venda':             'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  'Cadastro':              'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300',
  'Jurídico':              'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  'Indicador':             'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  'Marketing':             'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  'Tecnologia':            'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
  'Suporte e Atendimento': 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  'Motoboy':               'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
};

/** Recorte mínimo aceito pelos helpers abaixo — deixa passar tanto um Profile
 *  completo quanto o resultado de um `select('departamento, departamentos')`
 *  ou de um join reduzido (ex.: `responsavel:{ role, departamento }`). */
type PerfilComDepartamento = Pick<Profile, 'departamento' | 'departamentos'> & {
  funcao_comercial?: string | null;
};

const ehNomeComercialLegado = (d: string): boolean =>
  (DEPARTAMENTOS_COMERCIAIS_LEGADOS as readonly string[]).includes(d);

/** Nome do departamento para mostrar e agrupar: os nomes antigos do Comercial viram "Comercial". */
export const nomeDepartamento = (d: string): string =>
  ehNomeComercialLegado(d) ? DEPARTAMENTO_COMERCIAL : d;

/**
 * Cobre profiles antigos (`departamento` legado, valor único) e novos (`departamentos`, multi-valor).
 * Perguntar por "Comercial" também acha quem ainda está com um nome antigo do Comercial.
 */
export const temDepartamento = (p: PerfilComDepartamento | null | undefined, dept: string): boolean => {
  if (!p) return false;
  const confere = (d: string | null | undefined) => !!d && (d === dept || (dept === DEPARTAMENTO_COMERCIAL && ehNomeComercialLegado(d)));
  return (p.departamentos ?? []).some(confere) || confere(p.departamento);
};

/**
 * Gerente do Comercial (#6530): além do CRM, consulta o Painel Executivo, a Validação de
 * Dados (sem decidir) e o Inbox (só leitura). Gerente de outra área não herda esses acessos.
 */
export const ehGerenteComercial = (p: (PerfilComDepartamento & { role?: string | null }) | null | undefined): boolean =>
  p?.role === 'gerente' && temDepartamento(p, DEPARTAMENTO_COMERCIAL);

/**
 * Departamentos da pessoa para mostrar e agrupar em listas de seleção, sem
 * repetir e com os nomes antigos do Comercial já trocados por "Comercial".
 */
export const departamentosDaPessoa = (p: PerfilComDepartamento | null | undefined): string[] => {
  const brutos = p?.departamentos && p.departamentos.length > 0
    ? p.departamentos
    : p?.departamento ? [p.departamento] : [];
  return [...new Set(brutos.map(nomeDepartamento))];
};

/**
 * Função no Comercial (`profiles.funcao_comercial`, migration 20261029140000).
 * O Comercial é uma equipe só, com funções diferentes (decisão de 29/10/2026):
 * - Closer: recebe os leads que chegam sozinhos (Meta Ads, site, indicação) e fecha.
 * - BDR: prospecção ativa (Prospecção Geo e listas do motoboy); agenda ou avança
 *   a negociação e passa para a Closer.
 * - Hunter: vende na rua, como o vendedor externo, e pode supervisionar a equipe.
 * É a função, e não o campo "Cargo" (`job_title`, texto livre), que decide as
 * regras. A supervisão não depende da função: é permissão dada por pessoa.
 */
export const FUNCOES_COMERCIAIS = ['closer', 'bdr', 'hunter'] as const;
export type FuncaoComercial = typeof FUNCOES_COMERCIAIS[number];

export const ROTULO_FUNCAO_COMERCIAL: Record<FuncaoComercial, string> = {
  closer: 'Closer',
  bdr: 'BDR',
  hunter: 'Hunter',
};

export const funcaoComercial = (p: PerfilComDepartamento | null | undefined): FuncaoComercial | null => {
  const f = p?.funcao_comercial;
  return f && (FUNCOES_COMERCIAIS as readonly string[]).includes(f) ? f as FuncaoComercial : null;
};

/** Nome da função para mostrar ao lado da pessoa ("Closer", "BDR", "Hunter"); null fora do Comercial. */
export const rotuloFuncaoComercial = (p: PerfilComDepartamento | null | undefined): string | null => {
  const f = funcaoComercial(p);
  return f ? ROTULO_FUNCAO_COMERCIAL[f] : null;
};

/**
 * As funções que recebem e atendem lead pelo rodízio, com escala, SLA de primeiro
 * contato e pausa de SLA: Closer (leads que chegam sozinhos) e BDR (empresas
 * visitadas pelo motoboy). Para filtrar no banco: `.in('funcao_comercial', ...)`.
 */
export const FUNCOES_COM_RODIZIO: readonly FuncaoComercial[] = ['closer', 'bdr'];

/** É da equipe comercial? Pela função ou pelo departamento (inclusive os nomes antigos). */
export const ehDoComercial = (p: PerfilComDepartamento | null | undefined): boolean =>
  funcaoComercial(p) !== null || temDepartamento(p, DEPARTAMENTO_COMERCIAL);

/** Vende na rua (Hunter)? Sem função, quem ainda está no departamento antigo Comercial Externo. */
export const ehHunter = (p: PerfilComDepartamento | null | undefined): boolean => {
  const funcao = funcaoComercial(p);
  if (funcao) return funcao === 'hunter';
  return !!p && ((p.departamentos ?? []).includes('Comercial Externo') || p.departamento === 'Comercial Externo');
};

/**
 * Trabalha como Comercial Interno (Closer ou BDR)? Fonte única — antes disto
 * existiam cópias divergentes do mesmo teste espalhadas pelo projeto:
 * PerformanceDashboard.tsx só olhava `departamento` singular;
 * NegotiationWizard.tsx tinha a variante certa mas inline; e a Folha de
 * Pagamento (api.ts `getVendedores`) filtrava só por `role === 'vendedor'` e
 * não enxergava este departamento de jeito nenhum — é o que fazia o motor de
 * comissão nunca rodar pra Júlia Cintra, cujo role é `analista`.
 *
 * A função vence o departamento: Closer e BDR são internos (a negociação do BDR
 * nasce no plano do Comercial Interno porque quem fecha é a Closer); Hunter é
 * externo. Sem função, vale o departamento ('Vendas Internas' é o nome legado).
 */
export const ehComercialInterno = (p: PerfilComDepartamento | null | undefined): boolean => {
  const funcao = funcaoComercial(p);
  if (funcao) return funcao === 'closer' || funcao === 'bdr';
  if (!p) return false;
  const legado = (d: string | null | undefined) => d === 'Comercial Interno' || d === 'Vendas Internas';
  return (p.departamentos ?? []).some(legado) || legado(p.departamento);
};

/**
 * `departamento` é texto livre no banco — um "Pos Venda"/"Pós-Venda" digitado no
 * cadastro tiraria o acesso de alguém em silêncio. Espelha
 * public.normalizar_departamento(text) (migration 20260730110000).
 * Se mudar aqui, mude lá também.
 */
export const normalizarDepartamento = (txt?: string | null): string =>
  (txt ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/-/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Único departamento autorizado a liberar "Aprovado com Ressalvas" sobre
 * pendências das etapas 1-3 da Análise Cadastral (decisão de 29/07/2026 — o gate
 * é por departamento, não por papel: nem `adm` libera).
 *
 * Isto aqui só decide se o botão aparece. O controle de acesso de verdade é o
 * trigger trg_analise_liberacao_gate no Postgres.
 */
export const podeLiberarAprovacaoComRessalvas = (p: Profile | null | undefined): boolean => {
  if (!p) return false;
  const alvo = normalizarDepartamento('Pós Venda');
  return normalizarDepartamento(p.departamento) === alvo
    || (p.departamentos ?? []).some(d => normalizarDepartamento(d) === alvo);
};
